import {
  PICTURE_CHOICE_ELEMENT_TYPE,
  PICTURE_CHOICE_DISPLAY_TYPE,
  PICTURE_CHOICE_DEFAULT_COLUMNS,
  PICTURE_CHOICE_DEFAULT_IMAGE_FIT,
  PICTURE_CHOICE_MAX_COLUMNS,
  PICTURE_CHOICE_MIN_COLUMNS,
  PICTURE_CHOICE_MOBILE_MAX_COLUMNS,
  PICTURE_CHOICE_OTHER_VALUE,
  type PictureChoiceAltTextSource,
  type PictureChoiceCustomFieldPayload,
  type PictureChoiceDataType,
  type PictureChoiceElement,
  type PictureChoiceImageFit,
  type PictureChoiceOption,
  type PictureChoiceSelectionLimit,
  type PictureChoiceValidationError,
  type PictureChoiceValidationResult,
} from "@/types/pictureChoice";

interface PictureChoiceFieldLike {
  dataType?: string;
  optionDisplayType?: string;
  uuid?: unknown;
  id?: unknown;
  Id?: unknown;
  _id?: unknown;
  fieldKey?: unknown;
  hiddenFieldQueryKey?: unknown;
  customEdited?: boolean;
}

const PICTURE_CHOICE_IDENTITY_KEYS = [
  "uuid",
  "id",
  "Id",
  "_id",
  "fieldKey",
  "hiddenFieldQueryKey",
] as const;

const PICTURE_CHOICE_DATA_TYPES = new Set<PictureChoiceDataType>([
  "RADIO",
  "CHECKBOX",
]);
const PICTURE_CHOICE_IMAGE_FITS = new Set<PictureChoiceImageFit>([
  "cover",
  "contain",
  "fill",
]);

export const isPictureChoiceField = (
  field: PictureChoiceFieldLike | null | undefined
): boolean =>
  PICTURE_CHOICE_DATA_TYPES.has(field?.dataType as PictureChoiceDataType) &&
  field?.optionDisplayType === PICTURE_CHOICE_DISPLAY_TYPE;

export const isPictureChoiceSelectionTypeLocked = (
  field: PictureChoiceFieldLike | null | undefined
): boolean =>
  Boolean(field?.id || field?.Id || field?._id || field?.customEdited);

/** Returns the first persisted or builder-generated identity for an element. */
export const getPictureChoiceElementKey = (
  field: PictureChoiceFieldLike | null | undefined
): string | undefined => {
  for (const key of PICTURE_CHOICE_IDENTITY_KEYS) {
    const value = field?.[key];
    if (typeof value === "string" && value.trim()) {
      return `${key}:${value}`;
    }
  }

  return undefined;
};

export const normalizePictureChoiceOption = (
  option: Partial<PictureChoiceOption> | null | undefined,
  fallbackLabel = ""
): PictureChoiceOption => {
  const label = option?.label?.toString() ?? fallbackLabel;
  const hasStoredAltText = typeof option?.altText === "string";
  const altTextSource: PictureChoiceAltTextSource =
    option?.altTextSource ?? (hasStoredAltText ? "CUSTOM" : "AUTO");

  return {
    label,
    src: option?.src?.toString() ?? "",
    ...(option?.thumbnailUrl
      ? { thumbnailUrl: option.thumbnailUrl.toString() }
      : {}),
    ...(option?.createdAt ? { createdAt: option.createdAt.toString() } : {}),
    altText: hasStoredAltText ? option.altText ?? "" : label,
    altTextSource,
  };
};

export const createPictureChoiceOtherOption = (): PictureChoiceOption => ({
  label: PICTURE_CHOICE_OTHER_VALUE,
  src: "",
  altText: PICTURE_CHOICE_OTHER_VALUE,
  altTextSource: "AUTO",
});

export const createPictureChoiceOption = (label: string): PictureChoiceOption =>
  normalizePictureChoiceOption({ label, src: "" });

export const createUniquePictureChoiceOptionLabel = (
  options: Array<Pick<PictureChoiceOption, "label">>,
  prefix = "Option"
): string => {
  const existingLabels = new Set(
    options.map((option) => String(option?.label ?? "").trim())
  );
  let index = options.length + 1;
  let label = `${prefix} ${index}`;

  while (existingLabels.has(label) || label === PICTURE_CHOICE_OTHER_VALUE) {
    index += 1;
    label = `${prefix} ${index}`;
  }

  return label;
};

const getDefaultSelectionLimit = (): PictureChoiceSelectionLimit => ({
  mode: "unlimited",
});

export const clampPictureChoiceColumns = (value: unknown): number => {
  const columns = Number(value ?? PICTURE_CHOICE_DEFAULT_COLUMNS);
  if (!Number.isFinite(columns)) return PICTURE_CHOICE_DEFAULT_COLUMNS;

  return Math.min(
    Math.max(Math.trunc(columns), PICTURE_CHOICE_MIN_COLUMNS),
    PICTURE_CHOICE_MAX_COLUMNS
  );
};

export const getPictureChoiceMaxColumnsForDevice = (
  deviceMode: unknown
): number =>
  deviceMode === "mobile"
    ? PICTURE_CHOICE_MOBILE_MAX_COLUMNS
    : PICTURE_CHOICE_MAX_COLUMNS;

export const clampPictureChoiceColumnsForDevice = (
  value: unknown,
  deviceMode: unknown
): number =>
  Math.min(
    clampPictureChoiceColumns(value),
    getPictureChoiceMaxColumnsForDevice(deviceMode)
  );

export const createDefaultPictureChoiceElement = (): PictureChoiceElement => ({
  label: "Picture Choice",
  tag: "picture_choice",
  hiddenFieldQueryKey: "picture_choice",
  type: "picture_choice",
  typeLabel: "Picture Choice",
  dataType: "RADIO",
  builderElementType: PICTURE_CHOICE_ELEMENT_TYPE,
  optionDisplayType: PICTURE_CHOICE_DISPLAY_TYPE,
  placeholder: "",
  required: false,
  standard: false,
  custom: true,
  customEdited: false,
  allowCustomOption: false,
  spreadColumns: true,
  columnsNumber: PICTURE_CHOICE_DEFAULT_COLUMNS,
  picklistOptionsImage: ["Option 1", "Option 2", "Option 3"].map((label) =>
    createPictureChoiceOption(label)
  ),
  pictureChoiceSettings: {
    imageFit: PICTURE_CHOICE_DEFAULT_IMAGE_FIT,
    showOptionLabel: true,
    selectionLimit: getDefaultSelectionLimit(),
  },
});

export const normalizePictureChoiceElement = <T extends Record<string, any>>(
  field: T
): T | (T & PictureChoiceElement) => {
  if (!isPictureChoiceField(field)) {
    return field;
  }

  const {
    picklistImageOptions,
    isAllowedCustomOption,
    pictureChoiceSettings,
    ...rest
  } = field;
  const rawOptions = Array.isArray(field.picklistOptionsImage)
    ? field.picklistOptionsImage
    : Array.isArray(picklistImageOptions)
    ? picklistImageOptions
    : [];
  const allowCustomOption =
    field.allowCustomOption ?? isAllowedCustomOption ?? false;
  const { otherOption: _storedOtherOption, ...normalizedSettings } =
    pictureChoiceSettings ?? {};
  const normalizedOtherOption = allowCustomOption
    ? createPictureChoiceOtherOption()
    : undefined;
  const imageFit = normalizedSettings.imageFit;

  return {
    ...rest,
    builderElementType: PICTURE_CHOICE_ELEMENT_TYPE,
    dataType: field.dataType as PictureChoiceDataType,
    optionDisplayType: PICTURE_CHOICE_DISPLAY_TYPE,
    picklistOptionsImage: rawOptions.map((option: PictureChoiceOption) =>
      normalizePictureChoiceOption(option)
    ),
    allowCustomOption,
    spreadColumns: field.spreadColumns ?? true,
    columnsNumber: clampPictureChoiceColumns(field.columnsNumber),
    pictureChoiceSettings: {
      ...normalizedSettings,
      imageFit: PICTURE_CHOICE_IMAGE_FITS.has(
        imageFit as PictureChoiceImageFit
      )
        ? (imageFit as PictureChoiceImageFit)
        : PICTURE_CHOICE_DEFAULT_IMAGE_FIT,
      showOptionLabel: normalizedSettings.showOptionLabel ?? true,
      selectionLimit: {
        ...(normalizedSettings.selectionLimit ?? getDefaultSelectionLimit()),
      },
      ...(normalizedOtherOption ? { otherOption: normalizedOtherOption } : {}),
    },
  } as T & PictureChoiceElement;
};

export const normalizePictureChoiceElements = <T extends Record<string, any>>(
  fields: T[] | null | undefined
): Array<T | (T & PictureChoiceElement)> =>
  (fields ?? []).map((field) => normalizePictureChoiceElement(field));

export const normalizePictureChoiceSlidesInPlace = <
  T extends { slideData?: Record<string, any>[] }
>(
  slides: T[] | null | undefined
): T[] => {
  const normalizedSlides = slides ?? [];

  normalizedSlides.forEach((slide) => {
    if (!Array.isArray(slide.slideData)) return;

    const normalizedElements = normalizePictureChoiceElements(
      slide.slideData
    );
    slide.slideData.splice(0, slide.slideData.length, ...normalizedElements);
  });

  return normalizedSlides;
};

export const updatePictureChoiceOptionLabel = (
  option: PictureChoiceOption,
  label: string
): PictureChoiceOption => ({
  ...option,
  label,
  altText: option.altTextSource === "AUTO" ? label : option.altText,
});

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

export const validatePictureChoiceElement = (
  element: PictureChoiceElement | Record<string, any>
): PictureChoiceValidationResult => {
  const errors: PictureChoiceValidationError[] = [];
  const options = Array.isArray(element.picklistOptionsImage)
    ? element.picklistOptionsImage
    : [];

  if (options.length === 0) {
    errors.push({ code: "MISSING_OPTION_IMAGE", optionIndex: 0 });
  }

  const seenLabels = new Set<string>(
    element.allowCustomOption ? [PICTURE_CHOICE_OTHER_VALUE] : []
  );
  options.forEach((option: PictureChoiceOption, optionIndex: number) => {
    if (!option?.src?.trim()) {
      errors.push({ code: "MISSING_OPTION_IMAGE", optionIndex });
    }

    const label = option?.label?.trim() ?? "";
    if (!label) {
      errors.push({ code: "MISSING_OPTION_LABEL", optionIndex });
    } else if (seenLabels.has(label)) {
      errors.push({ code: "DUPLICATE_OPTION_LABEL", optionIndex });
    } else {
      seenLabels.add(label);
    }
  });

  if (element.dataType === "CHECKBOX") {
    const selectableOptionCount =
      options.length + (element.allowCustomOption ? 1 : 0);
    const selectionLimit = element.pictureChoiceSettings?.selectionLimit;

    if (
      selectionLimit?.mode === "exact" &&
      (!isPositiveInteger(selectionLimit.exact) ||
        selectionLimit.exact > selectableOptionCount)
    ) {
      errors.push({ code: "INVALID_EXACT_LIMIT" });
    }

    if (
      selectionLimit?.mode === "range" &&
      (!isPositiveInteger(selectionLimit.min) ||
        !isPositiveInteger(selectionLimit.max) ||
        selectionLimit.min > selectionLimit.max ||
        selectionLimit.max > selectableOptionCount)
    ) {
      errors.push({ code: "INVALID_RANGE_LIMIT" });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const buildPictureChoiceCustomFieldPayload = (
  element: PictureChoiceElement | Record<string, any>
): PictureChoiceCustomFieldPayload => ({
  dataType: element.dataType as PictureChoiceDataType,
  optionDisplayType: PICTURE_CHOICE_DISPLAY_TYPE,
  allowCustomOption: Boolean(element.allowCustomOption),
  picklistOptionsImage: (Array.isArray(element.picklistOptionsImage)
    ? element.picklistOptionsImage
    : []
  ).map((option: PictureChoiceOption) => ({
    label: option?.label ?? "",
    src: option?.src ?? "",
    ...(option?.thumbnailUrl ? { thumbnailUrl: option.thumbnailUrl } : {}),
    ...(option?.createdAt ? { createdAt: option.createdAt } : {}),
  })),
});

const pictureChoicePayloadsMatch = (
  left: PictureChoiceCustomFieldPayload,
  right: PictureChoiceCustomFieldPayload
): boolean =>
  left.dataType === right.dataType &&
  left.optionDisplayType === right.optionDisplayType &&
  left.allowCustomOption === right.allowCustomOption &&
  left.picklistOptionsImage.length === right.picklistOptionsImage.length &&
  left.picklistOptionsImage.every((option, index) => {
    const other = right.picklistOptionsImage[index];
    return (
      option.label === other?.label &&
      option.src === other?.src &&
      option.thumbnailUrl === other?.thumbnailUrl &&
      option.createdAt === other?.createdAt
    );
  });

export const preparePictureChoiceElementUpdate = <
  T extends PictureChoiceElement | Record<string, any>
>(
  currentElement: T,
  updatedElement: T
): { nextElement: T; apiFieldsChanged: boolean } => {
  const nextElement = isPictureChoiceSelectionTypeLocked(currentElement)
    ? ({
        ...updatedElement,
        dataType: currentElement.dataType,
        type: currentElement.type,
      } as T)
    : updatedElement;

  return {
    nextElement,
    apiFieldsChanged: !pictureChoicePayloadsMatch(
      buildPictureChoiceCustomFieldPayload(currentElement),
      buildPictureChoiceCustomFieldPayload(nextElement)
    ),
  };
};

export const mergePictureChoiceCustomFieldResponse = <
  T extends Record<string, any>,
  R extends Record<string, any>
>(
  currentElement: T,
  apiField: R
): T & R & PictureChoiceElement => {
  const apiOptions = Array.isArray(apiField.picklistOptionsImage)
    ? apiField.picklistOptionsImage
    : Array.isArray(apiField.picklistImageOptions)
    ? apiField.picklistImageOptions
    : undefined;
  const currentOptions = Array.isArray(currentElement.picklistOptionsImage)
    ? currentElement.picklistOptionsImage
    : [];
  const responseOptions = apiOptions ?? currentOptions;
  const matchedCurrentOptionIndexes = new Set<number>();
  const matchedCurrentOptions = new Map<number, PictureChoiceOption>();
  const claimMatch = (responseIndex: number, currentIndex: number) => {
    matchedCurrentOptionIndexes.add(currentIndex);
    matchedCurrentOptions.set(responseIndex, currentOptions[currentIndex]);
  };

  responseOptions.forEach(
    (option: Partial<PictureChoiceOption>, responseIndex: number) => {
      const exactMatchIndex = currentOptions.findIndex(
        (currentOption: PictureChoiceOption, currentIndex: number) =>
          !matchedCurrentOptionIndexes.has(currentIndex) &&
          currentOption.label === option.label &&
          currentOption.src === option.src
      );
      if (exactMatchIndex >= 0) {
        claimMatch(responseIndex, exactMatchIndex);
      }
    }
  );

  const claimUniqueMatches = (
    getKey: (option: Partial<PictureChoiceOption>) => string | undefined
  ) => {
    const responseIndexesByKey = new Map<string, number[]>();
    const currentIndexesByKey = new Map<string, number[]>();

    responseOptions.forEach(
      (option: Partial<PictureChoiceOption>, responseIndex: number) => {
        if (matchedCurrentOptions.has(responseIndex)) return;

        const key = getKey(option);
        if (!key) return;

        responseIndexesByKey.set(key, [
          ...(responseIndexesByKey.get(key) ?? []),
          responseIndex,
        ]);
      }
    );
    currentOptions.forEach(
      (currentOption: PictureChoiceOption, index: number) => {
        if (matchedCurrentOptionIndexes.has(index)) return;

        const key = getKey(currentOption);
        if (!key) return;

        currentIndexesByKey.set(key, [
          ...(currentIndexesByKey.get(key) ?? []),
          index,
        ]);
      }
    );

    responseIndexesByKey.forEach((responseIndexes, key) => {
      const currentIndexes = currentIndexesByKey.get(key) ?? [];
      if (responseIndexes.length === 1 && currentIndexes.length === 1) {
        claimMatch(responseIndexes[0], currentIndexes[0]);
      }
    });
  };

  claimUniqueMatches((option) => option.label);
  claimUniqueMatches((option) => option.src);

  const mergedOptions = responseOptions.map(
    (option: Partial<PictureChoiceOption>, responseIndex: number) => {
      const currentOption = matchedCurrentOptions.get(responseIndex);
      return normalizePictureChoiceOption({
        ...option,
        altText: currentOption?.altText,
        altTextSource: currentOption?.altTextSource,
      });
    }
  );

  return normalizePictureChoiceElement({
    ...currentElement,
    ...apiField,
    picklistOptionsImage: mergedOptions,
    allowCustomOption:
      apiField.allowCustomOption ??
      apiField.isAllowedCustomOption ??
      currentElement.allowCustomOption,
    spreadColumns: currentElement.spreadColumns,
    columnsNumber: currentElement.columnsNumber,
    pictureChoiceSettings: currentElement.pictureChoiceSettings,
  }) as T & R & PictureChoiceElement;
};
